import * as React from "react";
import toast from "react-hot-toast";
import dynamic from "next/dynamic";
import Button from "@components/button";
import { OperationResult, useMutation } from "urql";
import {
  ResendVerifyPhoneOtpMutation,
  ResendVerifyPhoneOtpMutationVariables,
  VerifyPhoneMutation,
  VerifyPhoneMutationVariables,
} from "@graphql/types";
import useStore from "src/store";
import { useRouter } from "next/router";
import { ResendVerifyPhoneOTP } from "@graphql/mutations/auth.mutation";

// const isProd = process.env.NODE_ENV === "production";
const PinInput = dynamic(() => import("react-pin-input"), { ssr: false });

type Props = {
  setNextScreen: React.Dispatch<React.SetStateAction<number>>;
  phoneNumber: string;
  fetching: boolean;
  resend?: () => Promise<OperationResult<any>>;
  nextPage?: string;
  confirmSignUp?: (
    arg: VerifyPhoneMutationVariables
  ) => Promise<OperationResult<VerifyPhoneMutation>>;
};

const ConfirmationForm: React.FC<Props> = (props) => {
  const setAccessToken = useStore((state) => state.setAccessToken);
  const setRefreshToken = useStore((state) => state.setRefreshToken);
  const router = useRouter();

  const [, resendFn] = useMutation<
    ResendVerifyPhoneOtpMutation,
    ResendVerifyPhoneOtpMutationVariables
  >(ResendVerifyPhoneOTP);

  const [code, setCode] = React.useState<string>("");

  React.useEffect(() => {
    if (code.length === 5) {
      if (props.confirmSignUp) {
        props
          .confirmSignUp({ otp: code, phone: props.phoneNumber })
          .then((res) => {
            if (res.data?.verifyPhone.access_token) {
              setAccessToken(res.data.verifyPhone.access_token);
            }
            if (res.data?.verifyPhone.refresh_token) {
              setRefreshToken(res.data.verifyPhone.refresh_token);
            }
            if (props.nextPage) {
              router.push(`/${props.nextPage}`);
            } else {
              router.push(`/`);
            }
          })
          .catch((error) =>
            toast.error(<p className="toast">{error.message}</p>)
          );
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (code.length === 5) {
      if (props.confirmSignUp) {
        props
          .confirmSignUp({ otp: code, phone: props.phoneNumber })
          .then((res) => {
            if (res.data?.verifyPhone.access_token) {
              setAccessToken(res.data.verifyPhone.access_token);
            }
            if (res.data?.verifyPhone.refresh_token) {
              setRefreshToken(res.data.verifyPhone.refresh_token);
            }
            if (props.nextPage) {
              router.push(`/${props.nextPage}`);
            } else {
              router.push(`/`);
            }
          })
          .catch((error) =>
            toast.error(<p className="toast">{error.message}</p>)
          );
      }
    } else {
      toast.error(<p className="toast">OTP is too short!</p>);
    }
  };

  const handleResend = () => {
    resendFn({ phone: props.phoneNumber })
      .then((res) => {
        if (res.data) {
          toast.success(
            <p className="toast">
              OTP has been sent to{" "}
              <span className="font-bold">{props.phoneNumber}</span>
            </p>
          );
        }
      })
      .catch(() => {
        toast.error(
          <p className="toast">
            Couldn&apos;t send OTP to{" "}
            <span className="font-bold">{props.phoneNumber}</span>
          </p>
        );
      });
  };

  return (
    <div className="max-w-lg -mb-12 px-4 w-full">
      <div className="px-4 w-full">
        <h1 className="font-libre text-2xl mb-3">Confirmation</h1>
        <p className="text-base font-light font-sfpro">
          A confirmation OTP has been sent to your Whatsapp Account. Kindly
          input the digits or tap on the link to confirm your number
        </p>
        <form onSubmit={handleSubmit} className="mt-12">
          <PinInput
            length={5}
            focus
            initialValue=""
            // secret
            onChange={(value) => {
              setCode(value);
            }}
            type="numeric"
            inputMode="number"
            style={{
              padding: "10px",
              display: " flex",
              justifyContent: " center",
              alignItems: " center",
              gap: "0.5rem",
              width: "100%",
              maxWidth: "370px",
              margin: "0 auto",
            }}
            inputStyle={{
              borderColor: "#808080",
              width: "17.5%",
              height: "78px",
              fontSize: "2.25rem",
              borderRadius: "2px",
            }}
            inputFocusStyle={{
              borderColor: "#1fad3e",
              caretColor: "#1fad3e",
            }}
            onComplete={(value) => {
              if (value.length === 5) {
              }
            }}
            autoSelect={true}
            regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
          />

          <div className="w-full mt-12">
            <Button
              loader={props.fetching}
              disabled={props.fetching}
              type="submit"
              label="Confirm OTP"
            >
              Confirm OTP
            </Button>
          </div>

          <p className="font-sfpro mt-4 font-light text-sm mx-auto text-center">
            <span>
              Expires in{" "}
              <span className="text-inawo-green font-medium">15 minutes</span>.
            </span>
            <span>
              {" "}
              Didn’t get a message?{" "}
              <button
                onClick={handleResend}
                type="button"
                className="text-inawo-green font-medium underline"
              >
                RESEND
              </button>
            </span>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ConfirmationForm;
